// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { AppIcon } from '@shared/ui-kit/components/app-icon';
import { ListSuggestions } from '@shared/ui-kit/components/autocomplete';
import { useSearchAsset } from '@advisor-app/features/ai-builder/hooks/use-search-asset';
import { type FC, useState } from 'react';
import { assetsTypeSlice } from '@shared/data-access/slices';
import {
  type AssetsTypeState as AssetTypeRedux,
  AssetTypes,
} from '@shared/data-access/interfaces/redux/assets-type-state-types';
import { useKeyboardNavigation } from '../hooks/use-keyboard-navigation';
import type { AllowedAssetType } from './asset-selector';
import {
  type MutualFund,
  type ETF,
} from '@shared/data-access/interfaces/api/ai/screener';
import { useAppDispatch } from '@advisor-app/store';

interface SearchEtfsProps {
  selectedAssets: AssetTypeRedux[];
  assetType: AllowedAssetType;
  placeholder: string;
}

const assetTypeEntity: Record<AllowedAssetType, AssetTypes> = {
  etf: AssetTypes.ETFS,
  mutual_fund: AssetTypes.MUTUAL_FUNDS,
};

export const SearchAsset: FC<SearchEtfsProps> = ({
  selectedAssets,
  assetType,
  placeholder,
}) => {
  const dispatch = useAppDispatch();

  const [searchText, setSearchText] = useState('');
  const [renderSuggestions, setRenderSuggestions] = useState(false);

  const { isLoading, assets } = useSearchAsset(searchText, assetType);

  const handleSelectValue = (item: MutualFund | ETF | string) => {
    const ticker = typeof item === 'string' ? item : item.ticker;
    const asset = (assets as (MutualFund | ETF)[]).find(
      asset => asset.ticker === ticker,
    )!;
    /* istanbul ignore next */
    const { name, description = '' } = asset ?? {};

    const isAssetSelected = selectedAssets.find(asset =>
      asset.ticker.includes(ticker),
    );

    if (isAssetSelected) return;

    dispatch(
      assetsTypeSlice.actions.setAssetType({
        type: assetTypeEntity[assetType],
        ticker: ticker,
        name: name,
        description: description!,
      }),
    );
    setSearchText('');
  };

  const { suggestionIndex, handleKeyDown, resetSuggestionIndex } =
    useKeyboardNavigation({ handleSelectValue, items: assets });

  const handleOverList = () => resetSuggestionIndex();

  return (
    <div className="relative box-border w-64" data-testid="search-etf">
      <input
        className="input-rounded pl-10 pr-4"
        value={searchText}
        onFocus={() => setRenderSuggestions(true)}
        onChange={e => setSearchText(e.target.value)}
        onBlur={() => setRenderSuggestions(false)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        required
        type="text"
        autoComplete="off"
        data-testid="input"
      />
      <div className="absolute left-4 top-3">
        <AppIcon icon="Search" color="disabled" size="base" />
      </div>

      <ListSuggestions
        allTickers={searchText.length === 0 ? null : assets}
        handleSelectValue={handleSelectValue}
        isLoading={isLoading}
        renderSuggestions={renderSuggestions}
        suggestionIndex={suggestionIndex}
        handleOverList={handleOverList}
        typeOfAssetSearch="ticker"
      />
    </div>
  );
};
